// src/pages/AdminGoldTransactions.jsx
import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import { Calendar } from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import DateObject from 'react-date-object';
import 'react-multi-date-picker/styles/layouts/mobile.css';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Filter, 
  Calendar as CalendarIcon, 
  ArrowLeft,
  User,
  ShoppingCart,
  Package,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import Swal from 'sweetalert2';

const showAlert = (message, type) => {
  Swal.fire({
    text: message,
    icon: type,
    toast: true,
    position: 'top',
    timer: 3000,
    timerProgressBar: true,
    showConfirmButton: false,
    customClass: { popup: 'font-vazir' },
  });
};

const formatNumber = (value, isGold = false) => {
  if (!value || isNaN(parseFloat(value))) return '0';
  const num = parseFloat(value);
  return isGold
    ? num.toLocaleString('fa-IR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
    : num.toLocaleString('fa-IR');
};

const convertToLatinDigits = (input) => {
  if (!input) return input;
  const persianDigits = [/۰/g, /۱/g, /۲/g, /۳/g, /۴/g, /۵/g, /۶/g, /۷/g, /۸/g, /۹/g];
  const arabicDigits = [/٠/g, /١/g, /٢/g, /٣/g, /٤/g, /٥/g, /٦/g, /٧/g, /٨/g, /٩/g];
  let result = input;
  for (let i = 0; i < 10; i++) {
    result = result.replace(persianDigits[i], i).replace(arabicDigits[i], i);
  }
  return result;
};

const formatDateForBackend = (date, isEndDate = false) => {
  if (!date) return date;
  try {
    const latinDate = convertToLatinDigits(date);
    const dateObject = new DateObject({ date: latinDate, calendar: persian, locale: persian_fa });
    const gregorianDate = dateObject.toDate();
    if (isEndDate) gregorianDate.setHours(23, 59, 59, 999);
    else gregorianDate.setHours(0, 0, 0, 0);
    return gregorianDate.toISOString().replace(/\.\d{3}Z$/, '');
  } catch (err) {
    console.error('خطا در تبدیل تاریخ:', err.message);
    return date;
  }
};

const formatPersianDate = (date) => {
  try {
    return new DateObject({ date: new Date(date), calendar: persian, locale: persian_fa })
      .format('YYYY/MM/DD HH:mm');
  } catch {
    return '—';
  }
};

export default function AdminGoldTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    status: '',
    minWeight: '',
    maxWeight: '',
    minPricePerGram: '',
    maxPricePerGram: '',
    startDate: '',
    endDate: '',
  });
  const [tempFilters, setTempFilters] = useState({ ...filters });
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);
  const [loading, setLoading] = useState(false);
  const startCalendarRef = useRef(null);
  const endCalendarRef = useRef(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // ریسپانسیو
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // بستن تقویم با کلیک خارج
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (startCalendarRef.current && !startCalendarRef.current.contains(event.target)) {
        setShowStartCalendar(false);
      }
      if (endCalendarRef.current && !endCalendarRef.current.contains(event.target)) {
        setShowEndCalendar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // رفرش توکن
  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');
      const res = await api.post('/api/auth/refresh-token', { refreshToken });
      localStorage.setItem('authToken', res.data.accessToken);
      return res.data.accessToken;
    } catch (err) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      navigate('/login_page');
      return null;
    }
  };

  // دریافت تراکنش‌ها
  const fetchTransactions = async (appliedFilters) => {
    setLoading(true);
    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');

      const params = { ...appliedFilters };
      if (appliedFilters.startDate) params.startDate = formatDateForBackend(appliedFilters.startDate, false);
      if (appliedFilters.endDate) params.endDate = formatDateForBackend(appliedFilters.endDate, true);

      const res = await api.get('/api/adminGoldTransactions/gold-transactions', {
        headers: { Authorization: `Bearer ${newToken}` },
        params,
      });
      setTransactions(res.data);
    } catch (err) {
      showAlert(err.response?.data?.message || 'خطا در دریافت تراکنش‌ها', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/login_page');
      return;
    }
    fetchTransactions(filters);
  }, [token, navigate]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setTempFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date, field) => {
    if (date) {
      setTempFilters(prev => ({ ...prev, [field]: date.format('YYYY/MM/DD') }));
    } else {
      setTempFilters(prev => ({ ...prev, [field]: '' }));
    }
    if (field === 'startDate') setShowStartCalendar(false);
    if (field === 'endDate') setShowEndCalendar(false);
  };

  const getCalendarValue = (date) => {
    try {
      if (date) {
        return new DateObject({
          date: convertToLatinDigits(date),
          calendar: persian,
          locale: persian_fa,
          format: 'YYYY/MM/DD',
        });
      }
      return null;
    } catch {
      return null;
    }
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    if (tempFilters.startDate && tempFilters.endDate) {
      const start = new DateObject({ date: convertToLatinDigits(tempFilters.startDate), calendar: persian, locale: persian_fa }).toDate();
      const end = new DateObject({ date: convertToLatinDigits(tempFilters.endDate), calendar: persian, locale: persian_fa }).toDate();
      if (start > end) {
        showAlert('تاریخ شروع نمی‌تواند بعد از تاریخ پایان باشد', 'error');
        return;
      }
    }
    setFilters(tempFilters);
    fetchTransactions(tempFilters);
  };

  const handleResetFilters = () => {
    const reset = {
      search: '', type: '', status: '', minWeight: '', maxWeight: '',
      minPricePerGram: '', maxPricePerGram: '', startDate: '', endDate: ''
    };
    setTempFilters(reset);
    setFilters(reset);
    fetchTransactions(reset);
  };

  const getTypeText = (type) => type === 'buy' ? 'خرید' : 'فروش';
  const getStatusText = (status) => {
    const map = { approved: 'تأیید شده', rejected: 'رد شده' };
    return map[status] || status;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 px-4 py-8 font-vazir">
      <div className="max-w-7xl mx-auto">
        {/* برگشت */}
        <motion.button
          whileHover={{ x: -5 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-indigo-600 mb-6 text-sm font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          برگشت
        </motion.button>

        {/* هدر */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center text-white shadow-xl">
              <Filter className="w-9 h-9" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-800">لیست خرید و فروش طلا</h2>
          <p className="text-gray-600 mt-2">مشاهده و فیلتر کردن تمام تراکنش‌های طلا</p>
        </motion.div>

        {/* فیلترها */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-6 md:p-8 border border-white/30"
        >
          <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-3">
            <Filter className="w-6 h-6 text-indigo-600" />
            فیلترهای پیشرفته
          </h3>

          <form onSubmit={handleFilterSubmit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {/* جستجو */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">جستجو</label>
              <input
                type="text"
                name="search"
                value={tempFilters.search}
                onChange={handleFilterChange}
                placeholder="نام، شماره، شناسه..."
                className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
              />
            </div>

            {/* نوع */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">نوع تراکنش</label>
              <select
                name="type"
                value={tempFilters.type}
                onChange={handleFilterChange}
                className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
              >
                <option value="">همه نوع</option>
                <option value="buy">خرید</option>
                <option value="sell">فروش</option>
              </select>
            </div>

            {/* وضعیت */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">وضعیت</label>
              <select
                name="status"
                value={tempFilters.status}
                onChange={handleFilterChange}
                className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
              >
                <option value="">همه وضعیت</option>
                <option value="approved">تأیید شده</option>
                <option value="rejected">رد شده</option>
              </select>
            </div>

            {/* وزن */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">حداقل وزن</label>
                <input
                  type="number"
                  step="0.001"
                  name="minWeight"
                  value={tempFilters.minWeight}
                  onChange={handleFilterChange}
                  placeholder="گرم"
                  className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">حداکثر وزن</label>
                <input
                  type="number"
                  step="0.001"
                  name="maxWeight"
                  value={tempFilters.maxWeight}
                  onChange={handleFilterChange}
                  placeholder="گرم"
                  className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                />
              </div>
            </div>

            {/* قیمت هر گرم */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">حداقل قیمت/گرم</label>
                <input
                  type="number"
                  name="minPricePerGram"
                  value={tempFilters.minPricePerGram}
                  onChange={handleFilterChange}
                  placeholder="ریال"
                  className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">حداکثر قیمت/گرم</label>
                <input
                  type="number"
                  name="maxPricePerGram"
                  value={tempFilters.maxPricePerGram}
                  onChange={handleFilterChange}
                  placeholder="ریال"
                  className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                />
              </div>
            </div>

            {/* تاریخ شروع */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">از تاریخ</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  name="startDate"
                  value={tempFilters.startDate}
                  onChange={handleFilterChange}
                  placeholder="1404/01/01"
                  className="flex-1 px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                />
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  type="button"
                  onClick={() => setShowStartCalendar(!showStartCalendar)}
                  className="p-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl shadow-lg"
                >
                  <CalendarIcon className="w-5 h-5" />
                </motion.button>
              </div>
              <AnimatePresence>
                {showStartCalendar && (
                  <motion.div
                    ref={startCalendarRef}
                    initial={{ opacity: 0, scale: 0.9, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: -10 }}
                    className="absolute z-50 mt-2 right-0 bg-white rounded-2xl shadow-2xl p-4 border border-gray-100"
                  >
                    <Calendar
                      calendar={persian}
                      locale={persian_fa}
                      value={getCalendarValue(tempFilters.startDate)}
                      onChange={(d) => handleDateChange(d, 'startDate')}
                      className="rmdp-mobile"
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* تاریخ پایان */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">تا تاریخ</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  name="endDate"
                  value={tempFilters.endDate}
                  onChange={handleFilterChange}
                  placeholder="1404/01/01"
                  className="flex-1 px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                />
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  type="button"
                  onClick={() => setShowEndCalendar(!showEndCalendar)}
                  className="p-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl shadow-lg"
                >
                  <CalendarIcon className="w-5 h-5" />
                </motion.button>
              </div>
              <AnimatePresence>
                {showEndCalendar && (
                  <motion.div
                    ref={endCalendarRef}
                    initial={{ opacity: 0, scale: 0.9, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: -10 }}
                    className="absolute z-50 mt-2 right-0 bg-white rounded-2xl shadow-2xl p-4 border border-gray-100"
                  >
                    <Calendar
                      calendar={persian}
                      locale={persian_fa}
                      value={getCalendarValue(tempFilters.endDate)}
                      onChange={(d) => handleDateChange(d, 'endDate')}
                      className="rmdp-mobile"
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* دکمه‌ها */}
            <div className="flex gap-3 items-end">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="flex-1 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Filter className="w-5 h-5" />
                اعمال فیلتر
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="button"
                onClick={handleResetFilters}
                className="flex-1 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
              >
                <XCircle className="w-5 h-5" />
                بازنشانی
              </motion.button>
            </div>
          </form>
        </motion.div>

        {/* جدول */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-6 md:p-8 mt-8 border border-white/30 overflow-x-auto"
        >
          <div className="flex items-center justify-between mb-6">
            <p className="text-lg font-semibold text-gray-700">
              تعداد تراکنش‌ها: <span className="text-indigo-600">{transactions.length}</span>
            </p>
            {loading && <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />}
          </div>

          {transactions.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">هیچ تراکنشی یافت نشد</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 text-gray-700">
                    <th className="p-4 text-right font-bold">شناسه</th>
                    <th className="p-4 text-right font-bold">تاریخ</th>
                    <th className="p-4 text-right font-bold">کاربر</th>
                    <th className="p-4 text-right font-bold">نوع</th>
                    <th className="p-4 text-right font-bold">وضعیت</th>
                    <th className="p-4 text-right font-bold">وزن (گرم)</th>
                    <th className="p-4 text-right font-bold">قیمت/گرم</th>
                    <th className="p-4 text-right font-bold">مبلغ کل</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx, index) => {
                    const weight = tx.type === 'buy' ? tx.credit_gold : tx.debit_gold;
                    const amount = tx.type === 'buy' ? tx.debit_rial : tx.credit_rial;
                    return (
                      <motion.tr
                        key={tx.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="border-b border-gray-100 hover:bg-indigo-50/50 transition-all duration-200"
                      >
                        <td className="p-4 font-mono text-indigo-600">#{tx.id}</td>
                        <td className="p-4 text-gray-700">{formatPersianDate(tx.created_at)}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <User className="w-5 h-5 text-gray-500" />
                            <div>
                              <p className="font-semibold">{tx.first_name} {tx.last_name}</p>
                              <p className="text-xs text-gray-500">{tx.phone_number}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${
                            tx.type === 'buy'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-orange-100 text-orange-700'
                          }`}>
                            {tx.type === 'buy' ? <ShoppingCart className="w-4 h-4" /> : <Package className="w-4 h-4" />}
                            {getTypeText(tx.type)}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${
                            tx.status === 'approved'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {tx.status === 'approved' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                            {getStatusText(tx.status)}
                          </span>
                        </td>
                        <td className="p-4 font-mono text-gray-800">{formatNumber(weight, true)}</td>
                        <td className="p-4 font-mono text-gray-800">{formatNumber(tx.price_per_gram)}</td>
                        <td className="p-4 font-mono text-indigo-600 font-bold">{formatNumber(amount)}</td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>

        {/* فوتر */}
        <div className="text-center mt-10 text-xs text-gray-500">
          <p>© ۱۴۰۴ - ESF.Gold | پنل مدیریت</p>
        </div>
      </div>

      <style jsx>{`
        .font-vazir { font-family: 'Vazir', sans-serif !important; }
      `}</style>
    </div>
  );
}